import { debounce } from 'es-toolkit';
import { del as idbDel, get as idbGet, set as idbSet } from 'idb-keyval';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const usePatientsStore = create(() => ({
    currentPatientHealthProfile: null,
    loadingHealthProfile: false,
}));

export const useLocalisationStore = create(() => ({
    isLanguageSelectionEnabled: false,
}));

export const useLocationStore = create(() => ({
    locations: [],
    /**
     * Selected location
     *
     * @type {{
     *   name: string;
     *   key: string;
     *   instanceIds: string[];
     *   configs: { [key: string]: any }[];
     * }}
     */
    selectedLocation: undefined,
    loading: false,
    hasFailed: false,
    secondaryLocations: undefined,
    userInstanceConfigs: undefined,
}));

export const useTodaysAppointmentsStore = create(() => ({
    todaysAppointments: undefined,
    professionals: undefined,
    unknownAppointments: undefined,
    checkinData: {},
    professionalFormTemplates: undefined,
    hasFailed: false,
    showLegacyCheckinModal: false,
    showNewManualCheckinModal: false,
    showLegacyManualCheckinModal: false,
    showGlobalManualCheckinModal: false,
    usePcsForGlobalManualCheckin: false,
    selectedForms: {},
    requiredForms: {},
    code: null,
    appointmentTicketNumber: {},
    isTodaysAppointmentsLoading: true,
    isCheckinDataLoading: true,
}));

export const usePractitionersMappingStore = create(() => ({
    showAddMappingModal: false,
    professionals: undefined,
    professionalMappings: undefined,
    calendars: undefined,
    mapping: undefined,
}));

export const useAppointmentsMappingStore = create(() => ({
    showAddMappingModal: false,
    scheduleMappings: undefined,
    mapping: undefined,
}));
export const usePatientCallSystemStore = create(
    persist(
        () => ({
            /**
             * Array of authentication tokens
             *
             * @type {Record<string, {
             *   token: string;
             *   validUntil: number;
             *   instanceId: string;
             * }>}
             */
            authTokens: {},
            selectedPrinter: {},
        }),
        {
            name: 'patient-call-system-storage',
        }
    )
);

export const usePowerUserStore = create(() => ({
    lastUsedCert: null,
    showCertModal: false,
}));

export const useAdminConfigStore = create(() => ({
    renderedPages: undefined,
    featureAccess: {},
    customers: null,
    accessRequest: null,
}));

export const useAdminUsersStore = create(() => ({
    users: {},
    currentlyUpdatingUsers: {},
    activeAccessRequests: null,
    inactiveAccessRequests: null,
}));

export const useAnamnesisBuilderStore = create(() => ({
    problems: [],
    invalidQuestions: {},
}));

export const useProfessionalsStore = create(() => ({
    templateArray: [],
    finalRecords: [],
    childData: null,
    cachedUserData: null,
    professionalMappings: undefined,
    lightMappings: undefined,
}));

const initialPrismicStore = {
    // Prismic caching
    appVersion: null,
    timestamp: undefined,
    ref: undefined,

    // Users
    userScopes: null,
    userGroups: null,

    // Today's appointments
    todaysAppointmentsPrismicData: undefined,
    professionals: undefined,

    // Location
    instanceConfigs: undefined,

    // Anamnesis Builder
    questions: {},
    subQuestions: {},
    allQuestions: {},
    appointmentTypes: null,

    // Professionals
    consentFormDocuments: null,

    // Data Assets
    labels: [],

    // Whitelabel
    allCustomers: null,
    currentCustomer: null,
    locationHubs: [],

    // Forms
    treatmentContract: null,

    // Access Requests
    accessRequests: null,

    // Medical Records
    medicalRecords: null,

    // Patients App Config
    patientsAppConfig: null,

    // Patients App Config feature flags
    featureFlags: null,
};

const fallback = {
    getItem: (name) => {
        const value = localStorage.getItem(name);
        return value ? JSON.parse(value) : null;
    },
    setItem: (name, value) => {
        try {
            const serialized = JSON.stringify(value);
            if (serialized.length > 5000000) {
                // ~5MB
                console.debug(`[storage] value too large for localStorage: "${name}"`);
                return;
            }
            localStorage.setItem(name, serialized);
        } catch (e) {
            console.debug(`[storage] localStorage.setItem failed for "${name}":`, e);
        }
    },
    removeItem: (name) => {
        localStorage.removeItem(name);
    },
};

// Custom storage object with fallback
const createStorage = () => {
    return {
        getItem: async (name) => {
            console.debug(`[storage] getItem: "${name}"`);
            try {
                return (await idbGet(name)) || null;
            } catch {
                console.debug(`[storage] idb failed. Fallback to localStorage for "${name}"`);
                return fallback.getItem(name);
            }
        },
        setItem: debounce(async (name, value) => {
            console.debug(`[storage] setItem: "${name}"`);
            try {
                await idbSet(name, value);
            } catch {
                console.debug(`[storage] idb failed. Fallback to localStorage for "${name}"`);
                fallback.setItem(name, value);
            }
        }, 1000),
        removeItem: async (name) => {
            console.debug(`[storage] removeItem: "${name}"`);
            try {
                await idbDel(name);
            } catch {
                console.debug(`[storage] idb failed. Fallback to localStorage for "${name}"`);
                fallback.removeItem(name);
            }
        },
    };
};

export const usePrismicStore = create(
    persist(
        (set) => ({
            ...initialPrismicStore,
            resetStore: () => set({ ...initialPrismicStore }),
        }),
        { name: 'prismic-storage', storage: createJSONStorage(createStorage) }
    )
);

export const useCustomersStore = create(() => ({
    locationKeyMap: undefined,
    locationIdsByCustomerMap: undefined,
    customers: undefined,
}));
